import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  useInvitations,
  useRevokeInvitation,
  useResendInvitation,
} from '@/hooks/useInvitations';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS, es, fr, it } from 'date-fns/locale';
import type { Locale } from 'date-fns';

const LOCALE_MAP: Record<string, Locale> = {
  pt: ptBR,
  en: enUS,
  es,
  fr,
  it,
};

export function PendingInvitesList() {
  const { t, i18n } = useTranslation();
  const { data: invites = [], isLoading } = useInvitations();
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();
  const dateLocale = LOCALE_MAP[i18n.language] ?? ptBR;

  if (isLoading) return null;

  if (invites.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('invite.pending.title')}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {t('invite.pending.empty')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invite.pending.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {invites.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between border-b pb-3 last:border-b-0"
            >
              <div className="space-y-1">
                <div className="font-medium">{inv.email}</div>
                <div className="text-sm text-muted-foreground flex gap-2 items-center">
                  <Badge variant="secondary">
                    {t(`invite.dialog.roles.${inv.role}`)}
                  </Badge>
                  <span>
                    {t('invite.pending.columns.sentAt')}:{' '}
                    {formatDistanceToNow(new Date(inv.criado_em), {
                      locale: dateLocale,
                      addSuffix: true,
                    })}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => resend.mutate({ invitation_id: inv.id })}
                  disabled={resend.isPending}
                >
                  {t('invite.pending.actions.resend')}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="destructive">
                      {t('invite.pending.actions.revoke')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('invite.pending.revokeConfirmTitle')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('invite.pending.revokeConfirmBody')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => revoke.mutate({ invitation_id: inv.id })}
                      >
                        {t('invite.pending.actions.revoke')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
